import { Sidecar, Text, Box, Icon, Flex, IconButton } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { NotificationList } from '../../components/NotificationListV2';
import { useSelector } from 'react-redux';
import { alertSexpr, notificationText, segmentIdFromKey } from '../../utils';
import { ALERT_NOTIFICATION, generateRoutePath } from '@fullstory/routes';
import { AlertNotificationsResponse, AlertNotificationAndKey } from '@fullstory/client-api';
import { Facility, Notification } from '@fullstory/proto/fs/services/pinguin';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import {
  fullstorySelectors,
  getAllNotifications,
  SegmentsById,
  useSegmentAlertNotifications,
} from '@fullstory/redux';

const { getOrgId } = fullstorySelectors;

export interface NotificationsSidecarProps {
  open: boolean;
  onClose: () => void;
}

export const NotificationsSidecar: React.FC<NotificationsSidecarProps> = ({ open, onClose }) => {
  const orgId = useSelector(getOrgId);

  let notifications: Notification[] = useSelector(getAllNotifications);
  // Note: We can't put this call behind a flag, but it doesn't trigger any extra XHR requests.
  const { alertNotifications, segments, segmentAlertsLastSeen } = useSegmentAlertNotifications();
  // If the flag is turned on, merge segment alerts in as metric alerts.
  const segmentAsNotifications = segmentAlertsAsNotifications(
    orgId,
    alertNotifications,
    segments,
    new Date(segmentAlertsLastSeen),
  );
  notifications = merge(notifications, segmentAsNotifications);

  return (
    <Sidecar data-component="NotificationsSidecar" open={open} onClose={onClose} position="right">
      <Box padding={16} height={1} overflow="hidden">
        <Box marginBottom={16}>
          <Flex justifyContent="space-between">
            <Flex>
              <Box marginRight={12}>
                <Icon name="bell" size={20} />
              </Box>
              <Text appearance="heading3">
                <FormattedMessage defaultMessage="Notifications" id="NAidKb" />
              </Text>
            </Flex>
            <IconButton
              iconName="close"
              title={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahO' })}
              onClick={onClose}
            />
          </Flex>
        </Box>
        <NotificationList orgId={orgId} notifications={notifications} onClose={onClose} />
      </Box>
    </Sidecar>
  );
};

// Merge two notification lists, ordering by created date (most recent first)
const merge = (list1: Notification[], list2: Notification[]): Notification[] => {
  let merged: Notification[] = [];
  let idx1 = 0;
  let idx2 = 0;
  while (idx1 < list1.length && idx2 < list2.length) {
    const item1 = list1[idx1];
    const item2 = list2[idx2];
    // We shouldn't have any null items in the list, but if we do, just return the original list.
    if (!item1 || !item2) {
      return list1;
    }
    if (Timestamp.mustDate(item1.createdAt) > Timestamp.mustDate(item2.createdAt)) {
      merged.push(item1);
      idx1++;
    } else {
      merged.push(item2);
      idx2++;
    }
  }
  // Add remaining items from either list
  // (They should not both contain more items)
  merged = merged.concat(list1.slice(idx1));
  merged = merged.concat(list2.slice(idx2));
  return merged;
};

// Converts a Segment Alert notification into a Notification for display purposes.
const segmentAlertsAsNotifications = (
  orgId: string,
  segmentAlerts: AlertNotificationsResponse | null,
  segments: SegmentsById | null,
  lastSeen: Date,
): Notification[] => {
  if (!segmentAlerts || !segments) {
    return [];
  }
  return segmentAlerts?.Notifications.map((alert: AlertNotificationAndKey) => {
    const segmentId = segmentIdFromKey(alert.Key);
    const segment = segments[segmentId];
    const createdAt = new Date(alert.Notification.CreatedAt);
    const link = generateRoutePath(ALERT_NOTIFICATION, {
      orgId,
      segmentId,
      alertId: alert.Key.AlertId,
      search: alertSexpr(alert.Notification.Alert),
      notificationId: alert.Key.AlertNotificationId,
    });
    return {
      id: alert.Key.AlertNotificationId,
      configId: {
        facility: Facility.SEGMENT_ALERTS,
        triggerId: alert.Key.AlertId,
      },
      createdAt: Timestamp.fromDate(new Date(alert.Notification.CreatedAt)),
      count: 1,
      botNotifications: [],
      read: createdAt <= lastSeen,
      content: {
        title: segment?.segment?.segment?.name || '',
        preheader: '',
        content: notificationText(alert.Notification),
        ctaLabel: intl.formatMessage({ defaultMessage: 'View', id: 'FgydNe' }),
        ctaUrl: link,
      },
    };
  });
};
